<template>
    <section name="CreateEditNotice">
        <form-modal
            :title="title"
            v-model="modelValue"
            :submit-button="'Submit'"
            @submit="submitForm">
            <section class="vstack gap-3">
                <div>
                    <label for="agency">Agency *</label>
                    <SelectField
                        name="agency"
                        class="w-100"
                        v-model="form.agency"
                        :select-items="dcraOris"
                        mode="single"
                        :error="errors.agency"
                        :min="1"
                        @update:modelValue="handleAgencyChange()"></SelectField>
                </div>
                <div>
                    <label for="year">Year *</label>
                    <SelectField
                        name="year"
                        class="w-100"
                        v-model="form.year"
                        :select-items="years"
                        mode="single"
                        :error="errors.year"
                        :min="1"
                        @update:modelValue="handleYearUpdate()"></SelectField>
                </div>
                <div>
                    <label for="quarter">Quarter *</label>
                    <SelectField
                        name="quarter"
                        class="w-100"
                        v-model="form.quarter"
                        :select-items="quarters"
                        mode="single"
                        :error="errors.quarter"
                        :min="1"
                        :open-direction="'top'"></SelectField>
                </div>
            </section>
        </form-modal>
    </section>
</template>

<script setup lang="ts">
import {ref, computed, watch, onMounted} from "vue";
import {Death, DeathForm, DeathZeroReport, YearsQuartersAvailable} from "./schema";
import {useDcraStore} from "@/stores/dcraStore";
import FormModal from "../Misc/FormModal.vue";
import SelectField from "@/components/Misc/SelectField.vue";
import {BInput, BTextarea, BAgency, BDatePicker} from "@/components/Fields";
import {array, date, number, object, string, ValidationError} from "yup";
import {useFormValidation} from "@/validation";
import {SelectItem, UploadType, DatasetDetails} from "@/types";
import { BFilter, BFilterableField, dateTimeOperations } from "../BFilterEditor/BFilterEditor.types";

const modelValue = defineModel({type: Boolean});
const props = defineProps<{
    startDate: string;
}>();
var agencyStartDate = ref(new Date(props.startDate));
const emit = defineEmits(["dismiss", "submit", "update:modelValue"]);
var dcraStore = useDcraStore();

const {handleSubmit, errors, resetValidation} = useFormValidation();

const form = ref({
    agency: "",
    year: new Date().getFullYear(),
    quarter: 0,
} as DeathZeroReport);

const schema = object<DeathForm>({
    agency: string().required("Agency is a required field"),
    year: number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .min(agencyStartDate.value.getFullYear())
        .max(new Date().getFullYear())
        .required("Year is a required field"),
    quarter: number().required("Quarter is a required field"),
});

var dcraErrors = ref([] as string[]);

var dcraOris = ref([] as SelectItem[]);
var quarters = ref([] as SelectItem[]);
var years = ref([] as SelectItem[]);
var yearsAndQuartersForSelectedAgency = ref([] as YearsQuartersAvailable[]);
const title = "Add Zero Report";

//FUNctions

const fetchYearsForDropdown = () => {
    years.value = [] as SelectItem[];
    yearsAndQuartersForSelectedAgency.value = [] as YearsQuartersAvailable[];

    const startYear = agencyStartDate.value.getFullYear();
    const endYear = new Date().getFullYear();

    for (let y = startYear; y <= endYear; y++) {
        yearsAndQuartersForSelectedAgency.value.push({
            yearLabel: `${y}`,
            yearValue: y,
            quarters: GenerateQuartersForYears(y)
        });
    }

    yearsAndQuartersForSelectedAgency.value.forEach((f: YearsQuartersAvailable) => {
        if (f.quarters.length > 0) {
            years.value.push({label: f.yearLabel, value: f.yearValue} as SelectItem);
        }
    });
}

const GenerateQuartersForYears = (year: number) => {
    const quarterArray = [] as SelectItem[];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const currentQuarterIndex = Math.floor(currentMonth / 3); // 0..3
    const startQuarterIndex = Math.floor(agencyStartDate.value.getMonth() / 3); // 0..3

    if (year === agencyStartDate.value.getFullYear()) {
        if (year === currentYear) {
            // agency started this year — include quarters from the start quarter
            // up to (but not including) the current quarter
            for (let i = startQuarterIndex; i < currentQuarterIndex; i++) {
                quarterArray.push({label: `Q${i + 1}`, value: i + 1} as SelectItem);
            }
        } else {
            for (let i = startQuarterIndex; i < 4; i++) {
                quarterArray.push({label: `Q${i + 1}`, value: i + 1} as SelectItem);
            }
        }
    } else if (year < currentYear) {
        quarterArray.push({label: "Q1", value: 1} as SelectItem, {label: "Q2", value: 2} as SelectItem, {label: "Q3", value: 3} as SelectItem, {label: "Q4", value: 4} as SelectItem);
    } else if (year === currentYear) {
        // include quarters strictly before the current quarter
        for (let i = 0; i < currentQuarterIndex; i++) {
            quarterArray.push({label: `Q${i + 1}`, value: i + 1} as SelectItem);
        }
    }

    return quarterArray;
};

const submitForm = async () => {
    if (await handleSubmit(schema, form.value)) {
        await dcraStore.addZeroReport(form.value);

        window.location.href = "dataupload.aspx?source=8";
    }
};

const handleYearUpdate = () => {

    form.value.quarter = -1;    

    const match = yearsAndQuartersForSelectedAgency.value.find((w:YearsQuartersAvailable) => w.yearValue === form.value.year);

    if (match) {
        quarters.value = match.quarters;
    }
}

const getOriList = async () => {
    let fields= [{property:"StartDate",operations:dateTimeOperations},
                 {property:"EndDate",operations:dateTimeOperations}   
    ] as BFilterableField[];

    const now = new Date();
    const firstDay = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    
    let filters = [{property:"StartDate",operation:"lt",value:firstDay.toISOString().split('T')[0]}, 
                   {property:"EndDate",operation:"ge",value:now.toISOString().split('T')[0]}] as BFilter[];
    
    let data = await dcraStore.getOriListForDataset("DCRA", filters, fields);

    dcraOris.value = data.map(ori=> {
        return {label:`${ori.ori} - ${ori.label}`, value:ori.ori} as SelectItem;
    });
}



const handleAgencyChange = async () => {    
    
    form.value.year = -1;
    form.value.quarter = -1;
    
    let datasets = await dcraStore.fetchDatasetDetailsForOri(form.value.agency);
    let oriDetails= {} as DatasetDetails;

    datasets.forEach((ds: DatasetDetails) => {

            if (ds.name == "DCRA") {
                oriDetails = ds;
            }
        });

    if(oriDetails != undefined && oriDetails.agency_start_date){
        // Parse date string to avoid timezone shift issues
        const dateStr = typeof oriDetails.agency_start_date === 'string'
            ? oriDetails.agency_start_date.split('T')[0]
            : new Date(oriDetails.agency_start_date).toISOString().split('T')[0];
        const [year, month, day] = dateStr.split('-').map(Number);
        agencyStartDate.value = new Date(year, month - 1, day); // months are 0-indexed
    }

    fetchYearsForDropdown();
}

//LifeCycle Hooks
onMounted(async () => {
    await getOriList();
});

watch(
    () => form.value,
    async () => {
        
        try {
            if (errors.value.length == 0) {
                return;
            }

            errors.value = Array.isArray(errors.value) ? [] : {};
            await schema.validate(form.value, {abortEarly: false});
        } catch (err) {
            const error = err as ValidationError;
            if (error.name !== "ValidationError") throw err;

            // Build an object map of field -> message
            const newErrors: Record<string, string> = {};
            error.inner.forEach((it: ValidationError) => {
                if (it.path) newErrors[it.path] = it.message;
            });
            errors.value = newErrors;
        }
    },
    {deep: true}
);
</script>

<style scoped>
.dcra-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
fieldset {
    border: 1px solid #ccc;
    padding: 1rem;
}
legend {
    font-weight: bold;
}
label {
    display: block;
    margin-bottom: 0.25rem;
}
input,
select,
textarea {
    width: 100%;
    padding: 0.25rem;
    box-sizing: border-box;
}
button {
    align-self: flex-start;
    padding: 0.5rem 1rem;
}
</style>
